import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Save, User, Camera, Trash2, BookOpen, HelpCircle } from 'lucide-react';
import { z } from 'zod';
import { useOnboardingChecklist } from '@/hooks/useOnboardingChecklist';
import { sanitizeUrl } from '@/lib/security/urlSanitizer';

// Schema de validation Zod pour le profil utilisateur
const profileSchema = z.object({
  prenom: z.string().min(1, "Le prénom est requis").max(100, "100 caractères max"),
  nom: z.string().min(1, "Le nom est requis").max(100, "100 caractères max"),
});

export default function ProfileSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [entrepriseId, setEntrepriseId] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Hook pour la checklist - permet de la réafficher
  const { isVisible: isChecklistVisible, showChecklist } = useOnboardingChecklist(entrepriseId);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        setPrenom(user.user_metadata?.prenom || '');
        setNom(user.user_metadata?.nom || '');
        setAvatarUrl(user.user_metadata?.avatar_url || null);

        // Charger l'entreprise pour le hook checklist
        const { data: entreprise } = await supabase
          .from('entreprises')
          .select('id')
          .eq('proprietaire_user_id', user.id)
          .maybeSingle();

        if (entreprise) {
          setEntrepriseId(entreprise.id);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  // Upload de la photo de profil
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une image',
        variant: 'destructive',
      });
      return;
    }

    // Vérifier la taille (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'Erreur',
        description: 'L\'image ne doit pas dépasser 2 Mo',
        variant: 'destructive',
      });
      return;
    }

    setUploadingPhoto(true);
    try {
      // Nom du fichier unique - directement à la racine du bucket avec le user.id
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}.${fileExt}`;

      // Upload vers Supabase Storage (bucket: avatars)
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          upsert: true,
          cacheControl: '3600',
        });

      if (uploadError) throw uploadError;

      // Obtenir l'URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Mettre à jour les métadonnées utilisateur
      const { error: updateError } = await supabase.auth.updateUser({
        data: { avatar_url: publicUrl }
      });

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      toast({
        title: 'Photo mise à jour',
        description: 'Votre photo de profil a été enregistrée.',
      });
    } catch (error: any) {
      console.error('Erreur upload:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de télécharger la photo',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      // Reset l'input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Supprimer la photo de profil
  const handleRemovePhoto = async () => {
    if (!user) return;

    setUploadingPhoto(true);
    try {
      // Mettre à jour les métadonnées utilisateur (supprimer l'URL)
      const { error } = await supabase.auth.updateUser({
        data: { avatar_url: null }
      });

      if (error) throw error;

      setAvatarUrl(null);
      toast({
        title: 'Photo supprimée',
        description: 'Votre photo de profil a été supprimée.',
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer la photo',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    // Validation Zod
    const validation = profileSchema.safeParse({
      prenom: prenom.trim(),
      nom: nom.trim(),
    });

    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { prenom: validation.data.prenom, nom: validation.data.nom }
      });

      if (error) throw error;

      toast({
        title: 'Profil mis à jour',
        description: 'Vos informations ont été enregistrées.',
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de mettre à jour le profil',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>Informations personnelles</CardTitle>
              <CardDescription>
                Gérez vos informations de profil
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Photo de profil */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative group">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-border">
                {sanitizeUrl(avatarUrl) ? (
                  <img
                    src={sanitizeUrl(avatarUrl)!}
                    alt="Photo de profil"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-muted-foreground" />
                )}
              </div>
              {/* Overlay au hover */}
              {!uploadingPhoto && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Camera className="w-6 h-6 text-white" />
                </button>
              )}
              {/* Loader */}
              {uploadingPhoto && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>

            <div className="flex-1 space-y-2">
              <Label>Photo de profil</Label>
              <p className="text-sm text-muted-foreground">
                JPG, PNG ou GIF. 2 Mo maximum.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Changer
                </Button>
                {avatarUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemovePhoto}
                    disabled={uploadingPhoto}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>
              {/* Input file caché */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
          </div>

          <div className="border-t border-border pt-6" />

          {/* Prénom / Nom */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom</Label>
              <Input
                id="prenom"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                placeholder="Votre prénom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nom">Nom</Label>
              <Input
                id="nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Votre nom"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={user?.email || ''}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              L'email ne peut pas être modifié
            </p>
          </div>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section Aide - Bouton réafficher checklist (visible uniquement si masquée) */}
      {!isChecklistVisible && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <HelpCircle className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <CardTitle>Aide</CardTitle>
                <CardDescription>
                  Tutoriels et guide d'utilisation
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="font-medium">Checklist "Premiers pas"</p>
                  <p className="text-sm text-muted-foreground">
                    Réafficher le guide d'utilisation sur le dashboard
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  showChecklist();
                  toast({
                    title: 'Guide réactivé',
                    description: 'La checklist est de nouveau visible sur le dashboard.',
                  });
                }}
              >
                Réafficher
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
